// ============================================================================
// colliders.js — static environment collision for the arena. Landmarks register
// their footprint as a CIRCLE (towers, spires, the obelisk, boulders, the statue)
// or an oriented BOX (walls, cliffs, the bridge) when an arena is built; the
// player and every unit are then pushed out of them each tick (slide, don't
// stop). A uniform grid broadphase keeps unit resolution O(nearby colliders) so
// hundreds of soldiers against ~150 obstacles stays cheap. Rebuilt per level in
// battlefield.buildArena. Units keep colliding with EACH OTHER via the spatial
// hash (crowd_ai separate()); this is world-vs-mover only.
// ============================================================================

const CELL = 12;                 // broadphase cell size (m)
const key = (cx, cz) => cx * 100000 + cz;   // grid cell hash

export class Colliders {
  constructor() {
    this.list = [];              // { t:0 circle {x,z,r} | t:1 box {x,z,hx,hz,ca,sa} }
    this.grid = new Map();       // cellKey -> [collider indices]
    this._seen = new Int32Array(0);
    this._token = 0;
  }

  clear() { this.list.length = 0; this.grid.clear(); }

  _index(i, minx, minz, maxx, maxz) {
    const c0 = Math.floor(minx / CELL), c1 = Math.floor(maxx / CELL);
    const r0 = Math.floor(minz / CELL), r1 = Math.floor(maxz / CELL);
    for (let cx = c0; cx <= c1; cx++) for (let cz = r0; cz <= r1; cz++) {
      const k = key(cx, cz); let a = this.grid.get(k);
      if (!a) { a = []; this.grid.set(k, a); }
      a.push(i);
    }
  }

  circle(x, z, r) {
    if (!(r > 0)) return;
    const i = this.list.length;
    this.list.push({ t: 0, x, z, r });
    this._index(i, x - r, z - r, x + r, z + r);
  }

  // oriented box: centre (x,z), half-extents (hx,hz), rotation `a` (radians, Y)
  box(x, z, hx, hz, a) {
    const i = this.list.length;
    const ca = Math.cos(a), sa = Math.sin(a);
    this.list.push({ t: 1, x, z, hx, hz, ca, sa });
    const ex = Math.abs(hx * ca) + Math.abs(hz * sa), ez = Math.abs(hx * sa) + Math.abs(hz * ca);   // world AABB
    this._index(i, x - ex, z - ez, x + ex, z + ez);
  }

  // push a mover circle (ent.x, ent.z, rad) out of every nearby solid, in place.
  // A couple of relaxation passes settle corners without a full physics solver.
  resolve(ent, rad) {
    if (this.list.length === 0) return;
    if (this._seen.length < this.list.length) this._seen = new Int32Array(this.list.length);
    for (let pass = 0; pass < 2; pass++) {
      const tok = ++this._token;
      const c0 = Math.floor((ent.x - rad) / CELL), c1 = Math.floor((ent.x + rad) / CELL);
      const r0 = Math.floor((ent.z - rad) / CELL), r1 = Math.floor((ent.z + rad) / CELL);
      let moved = false;
      for (let cx = c0; cx <= c1; cx++) for (let cz = r0; cz <= r1; cz++) {
        const a = this.grid.get(key(cx, cz));
        if (!a) continue;
        for (let n = 0; n < a.length; n++) {
          const idx = a[n];
          if (this._seen[idx] === tok) continue;
          this._seen[idx] = tok;
          if (this.list[idx].t === 0 ? pushCircle(ent, rad, this.list[idx]) : pushBox(ent, rad, this.list[idx])) moved = true;
        }
      }
      if (!moved) break;
    }
  }
}

function pushCircle(ent, rad, c) {
  const dx = ent.x - c.x, dz = ent.z - c.z;
  const rr = rad + c.r;
  let d2 = dx * dx + dz * dz;
  if (d2 >= rr * rr) return false;
  let d = Math.sqrt(d2);
  if (d < 1e-4) { ent.x = c.x + rr; return true; }   // dead-centre: shove +x
  const push = (rr - d) / d;
  ent.x += dx * push; ent.z += dz * push;
  return true;
}

function pushBox(ent, rad, b) {
  // world → box-local (rotate by -angle)
  const dx = ent.x - b.x, dz = ent.z - b.z;
  const lx = dx * b.ca + dz * b.sa;
  const lz = -dx * b.sa + dz * b.ca;
  const inside = lx > -b.hx && lx < b.hx && lz > -b.hz && lz < b.hz;
  let nlx, nlz;
  if (inside) {
    // push out along the least-penetrating face
    const penX = b.hx - Math.abs(lx), penZ = b.hz - Math.abs(lz);
    if (penX < penZ) { nlx = (lx >= 0 ? b.hx : -b.hx) + (lx >= 0 ? rad : -rad); nlz = lz; }
    else { nlz = (lz >= 0 ? b.hz : -b.hz) + (lz >= 0 ? rad : -rad); nlx = lx; }
  } else {
    const clx = Math.max(-b.hx, Math.min(b.hx, lx)), clz = Math.max(-b.hz, Math.min(b.hz, lz));
    const ex = lx - clx, ez = lz - clz;
    const d2 = ex * ex + ez * ez;
    if (d2 >= rad * rad) return false;
    const d = Math.sqrt(d2) || 1e-4;
    nlx = clx + (ex / d) * rad; nlz = clz + (ez / d) * rad;
  }
  // box-local → world (rotate by +angle)
  ent.x = b.x + nlx * b.ca - nlz * b.sa;
  ent.z = b.z + nlx * b.sa + nlz * b.ca;
  return true;
}
